import { useMemo } from 'react';
import type { ValidationEngine } from '../ValidationEngine.interface';

/**
 * Detects whether any currently-invalid form fields fail validation rules scoped to a specific flow.
 *
 * This is a store-agnostic hook that accepts the active flow as a plain string parameter, making it
 * usable by any consumer regardless of how the flow is determined (Redux selector, prop, etc.).
 *
 * @param engine - The validation engine instance to inspect.
 * @param flow - The flow name to check (e.g. `'contract'`). Pass `null` or `undefined` when the
 *               flow is not active — the hook short-circuits and returns `false` immediately.
 * @param errorStateTrigger - A value that changes whenever engine error state changes (e.g. the
 *                            `formErrors` object from `useFormValidationWithState`). Changing this
 *                            value causes the hook to re-evaluate.
 *
 * ## Why `getAllErrors()` + `includePaths` instead of just `getHasFormErrors({ flow })`?
 *
 * `getHasFormErrors({ flow })` answers a *hypothetical* question: it re-evaluates every registered
 * path under the specified flow using `evaluatePathForFlow`, which does NOT read from or write to
 * the engine's `errorStore` cache. This is necessary because the cache is built under the engine's
 * current `ctx.flow` and cannot answer questions about a different flow.
 *
 * Calling it without `includePaths` would re-evaluate every field in the form — including fields
 * that are currently clean. The `getAllErrors()` pre-filter narrows the evaluation to only the paths
 * already known to be invalid under the active flow, avoiding unnecessary rule re-evaluation.
 */
export function useFlowErrorDetector(
    engine: ValidationEngine,
    flow: string | null | undefined,
    errorStateTrigger?: unknown
): boolean {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => {
        if (!flow) return false;

        const invalidPaths = Object.entries(engine.getAllErrors())
            .filter(([, r]) => Boolean(r) && r.valid === false)
            .map(([p]) => p);

        if (!invalidPaths.length) return false;

        return engine.getHasFormErrors({ flow, includePaths: invalidPaths });
    }, [engine, flow, errorStateTrigger]); // errorStateTrigger is intentionally opaque
}
